import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AlertTriangle, ArrowLeft, FileText, Globe, History, Layers, Loader2, Sparkles, TrendingDown, TrendingUp, X } from "lucide-react";
import toast from "react-hot-toast";

import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import Callout from "@/components/ui/Callout";
import { CenteredSpinner } from "@/components/shared/Loader";
import { cn } from "@/lib/utils";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { ComplianceService } from "@/services/complianceService";
import { getComplianceStatusMeta } from "@/pages/admin/compliance/constant";

/**
 * The checklist re-apply wizard (Phases 5D + 10).
 *
 * Two steps, and **Back never discards**. Step 1 picks who the edit applies to;
 * step 2 confirms the cost. Closing either one returns to the builder with the
 * officer's edits intact — nothing is written until the final confirm, so a
 * dismissed dialog can never commit the thing it was asking about.
 *
 * That is the one rule this modal exists to enforce. The officer may have spent
 * ten minutes restructuring a tree; a wizard that threw that away on Back would
 * be worse than no wizard.
 *
 * ## The two actions
 *
 * | Button | What it does |
 * |---|---|
 * | **Apply & Re-Review** | Saves the edit and sends affected documents back to review. No AI spend — the officer re-checks by hand. |
 * | **Re-Extract All** | The same, plus re-runs extraction where the generated prompt actually changed. |
 *
 * **Re-Extract All is hidden when there is nothing to re-extract.** Adding a
 * document or changing a weight leaves every existing file exactly as valid as
 * it was, so there is no prompt to rebuild and no file to re-read — the only
 * action is to request the new document from the candidate. Offering an AI
 * re-run there would name an operation that does nothing and then report "0
 * documents will be re-read", which reads as a broken dialog.
 *
 * "Apply & Re-Review" is deliberately NOT called "Save as it is". That phrase
 * means "change nothing about existing work" on the re-trigger screen, and here
 * the same words would hide the destructive half: approved documents go back to
 * Pending for Review either way. The label names both halves.
 *
 * When the edit adds a new document or section, both buttons gain
 * "& Request Documents" — the candidate has to upload something new, and no
 * amount of re-extraction can substitute for a file that does not exist yet.
 *
 * ## Why step 2 exists for both paths
 *
 * A reset deserves as much deliberation as a spend: it puts real approvals back
 * in someone's queue. Step 2 always shows what changed; it adds the token
 * estimate only when extraction is actually going to run.
 *
 * The estimate is a RANGE, and says so. Real cost is dominated by page count and
 * image size, which vary by an order of magnitude between a phone photo and a
 * six-page scan. A precise-looking number would just be a figure to be held to.
 */

const fmtTokens = (n) => {
  if (!n) return "0";
  if (n >= 1000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
};

/** Candidates past the compliance stage are listed but never pre-selected. */
const PRESELECT_EXCLUDED_STATUSES = new Set(["approved", "rejected"]);

const ChecklistReapplyModal = ({
  open,
  // Back / X / Esc: the officer is NOT done. Returns them to the builder with
  // their edits, and nothing is written. Deliberately not named `onClose`:
  // "close" reads as "we are finished here", and a success path that called it
  // reopened the builder on the edit it had just saved.
  //
  // Success has its own exit, `onApplied`. The two must never be the same prop.
  onBack,
  onApplied,
  checklistId,
  checklistName,
  currentVersion,
  // The draft edit from the builder, in the same shape the PUT would send.
  draft,
  // Checklist usage (Phase 10): the same in-flight counts the builder banner
  // shows. Repeated in the confirm step so the officer sees WHO a new version
  // affects at the moment of committing, not only while editing.
  usage,
  // Called when the preview comes back with nothing changed. Phase 10: a
  // no-op must not create a version, and it must not open a dialog about a
  // non-event either -- the builder just closes.
  onNoop,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState("REVIEW_ONLY");

  // Held in a ref, not read from props inside the callback. `onNoop` is an
  // inline arrow at the call site, so it has a new identity on every render;
  // as a dependency of `loadPreview` — which the mount effect depends on —
  // that is an infinite preview refetch. The ref keeps the latest callback
  // without making the callback's identity part of the dependency graph.
  const onNoopRef = useRef(onNoop);
  useEffect(() => {
    onNoopRef.current = onNoop;
  }, [onNoop]);

  const loadPreview = useCallback(async () => {
    if (!checklistId || !draft) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ComplianceService.previewChecklistReapply(checklistId, {
        name: draft.name,
        region: draft.region,
        sections: draft.sections,
      });
      const data = res?.preview ?? null;
      setPreview(data);
      // Phase 10. An edit that changed nothing is not a save: no version,
      // no snapshot, no audit row -- and no dialog. Opening a modal to
      // announce that nothing happened is the kind of friction that
      // teaches people to click through modals without reading them.
      if (data?.isNoop) {
        onNoopRef.current?.();
        return;
      }
      // Pre-select only candidates still mid-process. Including someone
      // who has already cleared compliance should be a deliberate act,
      // not the side effect of a Select-All default.
      setSelected(
        new Set(
          (data?.candidates || []).filter((c) => !PRESELECT_EXCLUDED_STATUSES.has(String(c.complianceStatus || "").toLowerCase())).map((c) => c.complianceCandidateId),
        ),
      );
    } catch (e) {
      setError(apiErrorMessageFormat(e, "Failed to work out what this edit affects"));
    } finally {
      setLoading(false);
    }
  }, [checklistId, draft]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setMode("REVIEW_ONLY");
      loadPreview();
    }
  }, [open, loadPreview]);

  const summary = preview?.changeSummary;
  const requiresRequest = Boolean(preview?.requiresRequest);

  // Totals for the CURRENT selection, not the whole checklist — the officer
  // is confirming what their own choice costs.
  const selectedRows = useMemo(() => (preview?.candidates || []).filter((c) => selected.has(c.complianceCandidateId)), [preview, selected]);
  const resetCount = useMemo(() => selectedRows.reduce((n, c) => n + (c.documentsToReset?.length || 0), 0), [selectedRows]);
  const reextractCount = useMemo(() => selectedRows.reduce((n, c) => n + (c.documentsToReextract?.length || 0), 0), [selectedRows]);

  const cap = preview?.cappedOut != null && reextractCount > 0 ? Math.max(0, reextractCount - (preview.totalReextract - preview.cappedOut)) : 0;

  const suffix = requiresRequest ? " & Request Documents" : "";

  // Is there anything for the AI to actually re-read?
  //
  // Adding a document changes nothing about the documents that already exist:
  // there is no file to re-extract, only a new one to request. Same for a
  // weight change -- that is arithmetic. Offering "Re-Extract All" in those
  // cases names an action that would do nothing, and the confirm step then
  // reads "0 document(s) will be re-read by AI", which is the wizard telling
  // the officer it is about to do nothing. So the option is hidden entirely
  // rather than shown as a no-op.
  const canReextract = reextractCount > 0;

  // What the primary action is called depends on what it will do. With
  // nothing to re-extract, applying the edit and requesting the new documents
  // IS the whole action, so it becomes the primary button.
  const applyLabel = requiresRequest ? "Apply & Request Documents" : "Apply & Re-Review";

  // Shape of the tree the officer is about to commit, for the metadata bar.
  const totalSections = draft?.sections?.length || 0;
  const totalDocuments = useMemo(() => (draft?.sections || []).reduce((n, s) => n + (s.subsections?.length || 0), 0), [draft]);

  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const goConfirm = (nextMode) => {
    setMode(nextMode);
    setStep(2);
  };

  const commit = async () => {
    setSaving(true);
    try {
      const res = await ComplianceService.commitChecklistReapply(checklistId, {
        name: draft.name,
        region: draft.region,
        sections: draft.sections,
        // The lost-update guard: the version the builder loaded.
        expectedVersion: currentVersion,
        mode,
        candidateIds: Array.from(selected),
      });
      const result = res?.result ?? {};
      toast.success(
        // Defensive: the wizard short-circuits a no-op before it ever
        // gets here, but another officer could land the identical edit
        // between preview and commit.
        result.noop
          ? "No changes to save."
          : `Checklist saved as v${result.version}.` +
              (result.summary?.reset ? ` ${result.summary.reset} document(s) sent back for review.` : "") +
              (result.summary?.queued ? ` ${result.summary.queued} queued for re-extraction.` : ""),
      );
      // `onApplied` alone. NOT `onClose` as well: the page wires that to
      // "reopen the builder with the draft", which is what Back has to mean —
      // so calling it here sent the officer back into the editor holding the
      // tree they had just committed. Saving from there re-sent an identical
      // payload, the server answered `isNoop`, and this wizard flashed open
      // and shut with "No changes to save." on an edit that had in fact saved.
      //
      // The same trap `RetriggerConfirmModal` names in its docstring, from the
      // other side: dismissing a confirmation must not commit, and succeeding
      // must not reopen. Ending the flow is `onApplied`'s job.
      onApplied?.(result);
    } catch (e) {
      // A 409 here means someone else saved while this wizard was open.
      // The server refuses rather than merging, so the officer keeps their
      // edits and is told what happened.
      toast.error(apiErrorMessageFormat(e, "Failed to apply the checklist update"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col rounded-xl border border-border-primary bg-background-secondary shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-border-primary px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-semibold text-text-primary">{step === 1 ? "Apply checklist changes" : "Confirm Changes & Version Update"}</h2>
            <p className="truncate text-[12px] text-text-secondary">
              {checklistName}
              {currentVersion != null && ` · v${currentVersion} → v${currentVersion + 1}`}
            </p>
          </div>
          <ButtonShadcn
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="Close"
            className="h-auto w-auto shrink-0 rounded-full p-1 text-text-secondary hover:bg-background-icon"
            title="Return to the builder — your edits are kept"
          >
            <X size={16} />
          </ButtonShadcn>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading && <CenteredSpinner />}
          {!loading && error && <p className="text-[13px] text-tone-rose">{error}</p>}

          {/* What the officer is versioning, stated once at the top and
              carried across both steps: which version this becomes, which
              region's prompts it drives, how big the tree is, and how many
              real people are mid-process against it right now. */}
          {!loading && !error && (
            <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-lg border border-border-primary/60 bg-background-primary/30 px-3 py-2 text-[11.5px]">
              <div className="flex items-center gap-1.5 font-medium text-text-primary">
                <History size={13} className="text-text-secondary" />
                <span>Version:</span>
                <span className="rounded bg-background-icon px-1.5 py-0.5 text-[10.5px] font-semibold text-text-primary">
                  v{currentVersion ?? 1} → v{(currentVersion ?? 1) + 1}
                </span>
              </div>
              {draft?.region && (
                <div className="flex items-center gap-1 border-l border-border-primary/60 pl-2.5 text-text-secondary">
                  <Globe size={12} />
                  <span>{draft.region}</span>
                </div>
              )}
              <div className="flex items-center gap-1 border-l border-border-primary/60 pl-2.5 text-text-secondary">
                <Layers size={12} />
                <span>
                  {totalSections} Section{totalSections === 1 ? "" : "s"}
                </span>
                <span>·</span>
                <FileText size={12} />
                <span>
                  {totalDocuments} Document{totalDocuments === 1 ? "" : "s"}
                </span>
              </div>
              {usage?.inFlightCount > 0 && (
                <div className="ml-auto flex items-center gap-1 text-[11px] font-medium text-tone-amber">
                  <span>
                    {usage.inFlightCount} candidate{usage.inFlightCount === 1 ? "" : "s"} in-flight
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Step 1: who does this apply to ─────────────────────── */}
          {!loading && !error && step === 1 && (
            <div className="flex flex-col gap-4">
              <ChangeList summary={summary} draft={draft} />

              {requiresRequest && (
                <Callout tone="neutral" icon={AlertTriangle} className="rounded-lg px-3 py-2.5 text-[12px]">
                  This edit adds a new requirement. Selected candidates will be asked to upload it through their portal, and their status returns to
                  <strong className="font-semibold"> Pending From Candidate</strong>.
                </Callout>
              )}

              {/* The cost, shown BEFORE the officer picks an
                                action rather than only in the confirm step.
                                Choosing between two buttons without knowing
                                what one of them spends is not a choice. */}
              {canReextract && (
                <div className="flex items-start gap-2 rounded-lg border border-tone-amber/30 bg-tone-amber-soft/30 px-3 py-2">
                  <Sparkles size={13} className="mt-0.5 shrink-0 text-tone-amber" />
                  <p className="text-[11.5px] text-text-primary">
                    Re-Extract would re-read <strong className="font-semibold">{reextractCount} document(s)</strong> — roughly {fmtTokens(reextractCount * 2500)}–
                    {fmtTokens(reextractCount * 6000)} tokens.
                  </p>
                </div>
              )}

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-[12.5px] font-medium text-text-primary">
                    Apply to candidates ({selected.size}/{preview?.candidates?.length || 0})
                  </p>
                  <p className="text-[11px] text-text-secondary">Unselected candidates keep the checklist version they were sent.</p>
                </div>

                {(preview?.candidates || []).length === 0 ? (
                  <p className="rounded-lg border border-border-primary/60 px-3 py-3 text-[12px] text-text-secondary">
                    No candidates are using this checklist. The edit affects nobody.
                  </p>
                ) : (
                  <div className="flex max-h-64 flex-col divide-y divide-border-primary/40 overflow-y-auto rounded-lg border border-border-primary/60">
                    {preview.candidates.map((c) => (
                      <label key={c.complianceCandidateId} className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 hover:bg-background-icon/30">
                        <Checkbox
                          checked={selected.has(c.complianceCandidateId)}
                          onCheckedChange={() => toggle(c.complianceCandidateId)}
                          className="mt-0.5 shrink-0"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-[12.5px] text-text-primary">{c.candidateName || "Unnamed candidate"}</span>
                            {/* The shared label map, not the raw enum. Stripping
                              underscores left "PENDING FOR REVIEW" shouting on
                              screen, and worse, a second spelling of a status the
                              rest of the module already has one name for. */}
                            {c.complianceStatus && (
                              <span className="shrink-0 rounded bg-background-icon px-1.5 py-0.5 text-[10px] text-text-secondary">
                                {getComplianceStatusMeta(c.complianceStatus).label}
                              </span>
                            )}
                            {c.pinnedVersion != null && <span className="shrink-0 text-[10.5px] text-text-secondary">on v{c.pinnedVersion}</span>}
                          </span>
                          {/* Say what actually happens to
                                                        THIS candidate. "No documents
                                                        affected" alone was misleading
                                                        on an edit that adds a new
                                                        requirement: nothing of theirs
                                                        is reset, but they are still
                                                        being asked to upload. */}
                          <span className="mt-0.5 block text-[11px] text-text-secondary">
                            {[
                              c.documentsToReset?.length ? `${c.documentsToReset.length} document(s) back to review` : null,
                              requiresRequest ? "will be asked for the new document(s)" : null,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "No documents affected"}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Step 2: confirm ────────────────────────────────────── */}
          {!loading && !error && step === 2 && (
            <div className="flex flex-col gap-4">
              {/* Phase 10's deliberate friction. The point is not
                                to ask "are you sure" -- it is to state the two
                                things the officer cannot see from the builder:
                                a permanent version is being created, and how
                                many real people are mid-process against this
                                checklist right now. */}
              <Callout tone="neutral" icon={AlertTriangle} className="rounded-lg px-3 py-2.5 text-[12px]">
                This creates <strong className="font-semibold">version {(currentVersion ?? 0) + 1}</strong> and is permanently recorded in the checklist version history.
                {usage?.inFlightCount > 0 && (
                  <>
                    {" "}
                    <strong className="font-semibold">
                      {usage.inFlightCount} candidate
                      {usage.inFlightCount === 1 ? " is" : "s are"} mid-process
                    </strong>{" "}
                    against this checklist
                    {usage.attachedDocumentCount > 0
                      ? `, with ${usage.attachedDocumentCount} document${usage.attachedDocumentCount === 1 ? "" : "s"} already uploaded`
                      : ""}
                    .
                  </>
                )}
              </Callout>

              <ChangeList summary={summary} draft={draft} />

              <div className="rounded-lg border border-border-primary/60 p-3">
                <p className="mb-2 text-[12.5px] font-medium text-text-primary">What will happen</p>
                <ul className="flex flex-col gap-1 text-[12px] text-text-secondary">
                  <li>
                    <strong className="font-semibold text-text-primary">{selected.size}</strong> candidate(s) affected
                  </li>
                  {resetCount > 0 && (
                    <li>
                      <strong className="font-semibold text-text-primary">{resetCount}</strong> document(s) return to Pending for Review and need approving again
                    </li>
                  )}
                  {/* Only listed when it is non-zero. A line
                                        reading "0 document(s) will be re-read"
                                        is noise that makes the officer doubt
                                        the rest of the list. */}
                  {mode === "REEXTRACT" && reextractCount > 0 && (
                    <li>
                      <strong className="font-semibold text-text-primary">{reextractCount}</strong> document(s) will be re-read by AI
                    </li>
                  )}
                  {requiresRequest && <li>Candidates will be asked to upload the new document(s)</li>}
                </ul>
              </div>

              {mode === "REEXTRACT" && reextractCount > 0 && (
                <div className="rounded-lg border border-tone-amber/30 bg-tone-amber-soft/30 p-3">
                  <p className="flex items-center gap-1.5 text-[12.5px] font-medium text-tone-amber">
                    <Sparkles size={13} /> Estimated AI usage
                  </p>
                  <p className="mt-1 text-[12px] text-text-primary">
                    {reextractCount} document(s) ·{" "}
                    <strong className="font-semibold">
                      roughly {fmtTokens(reextractCount * 2500)}–{fmtTokens(reextractCount * 6000)} tokens
                    </strong>
                  </p>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    An estimate, not a quote — real usage depends on how many pages each document has and whether it is a scan or a photo.
                  </p>
                  {cap > 0 && (
                    <p className="mt-1.5 text-[11px] font-medium text-tone-amber">
                      {cap} document(s) exceed the per-run limit and will be skipped. Run this again afterwards to cover them.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-primary px-5 py-3">
          {step === 2 && (
            <Button
              onClick={() => setStep(1)}
              disabled={saving}
              className="mr-auto flex items-center gap-1.5 rounded-full bg-background-icon px-4 py-2 text-[11px] text-text-primary hover:bg-background-icon/60 disabled:opacity-50"
            >
              <ArrowLeft size={12} /> Back
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={onBack}
              className="mr-auto flex items-center gap-1.5 rounded-full bg-background-icon px-4 py-2 text-[11px] text-text-primary hover:bg-background-icon/60"
            >
              <ArrowLeft size={12} /> Back
            </Button>
          )}

          {step === 1 && (
            <>
              {/* Only offered when documents actually need
                                re-reading. See `canReextract`. */}
              {canReextract && (
                <Button
                  onClick={() => goConfirm("REVIEW_ONLY")}
                  disabled={loading || !!error}
                  className="rounded-full bg-background-icon px-4 py-2 text-[11px] text-text-primary hover:bg-background-icon/60 disabled:opacity-50"
                >
                  {applyLabel}
                </Button>
              )}
              <Button
                onClick={() => goConfirm(canReextract ? "REEXTRACT" : "REVIEW_ONLY")}
                disabled={loading || !!error}
                className="rounded-full bg-button-secondary px-4 py-2 text-[11px] text-text-base disabled:opacity-50"
              >
                {canReextract ? `Re-Extract All${suffix}` : applyLabel}
              </Button>
            </>
          )}

          {step === 2 && (
            <Button
              onClick={commit}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-full bg-button-secondary px-4 py-2 text-[11px] text-text-base disabled:opacity-50"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {saving ? "Applying…" : mode === "REEXTRACT" ? `Confirm & Re-Extract${suffix}` : requiresRequest ? "Confirm & Request Documents" : "Confirm & Apply"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * What changed, grouped the way the officer thinks about it.
 *
 * Weight is shown, not just named. A document is not "added" in isolation --
 * it takes a share of a fixed 100, so every other document's share drops to
 * pay for it. Showing the allocation on the added row, the delta on the
 * changed row, and the whole resulting distribution underneath is the
 * difference between "something moved" and knowing what the tree now weighs.
 *
 * `draft` is the tree about to be committed; the summary carries the diff.
 * Weight comes from the summary where the server sent it, and falls back to
 * the draft so an older payload still renders the allocation.
 */
const ChangeList = ({ summary, draft }) => {
  const { docMap, allDraftDocs, totalWeight } = useMemo(() => {
    const map = new Map();
    const docs = [];
    let sum = 0;
    for (const sec of draft?.sections || []) {
      for (const sub of sec.subsections || []) {
        if (!sub.name) continue;
        const info = { name: sub.name, section: sec.name, weight: Number(sub.weightPercentage ?? 0), isRequired: sub.isRequired !== false };
        sum += info.weight;
        map.set(sub.name.trim().toLowerCase(), info);
        docs.push(info);
      }
    }
    return { docMap: map, allDraftDocs: docs, totalWeight: sum };
  }, [draft]);

  const d = summary?.documents || {};
  const s = summary?.sections || {};

  const addedItems = useMemo(() => {
    const list = (s.added || []).map((n) => ({ type: "section", text: `Section: ${n}` }));
    for (const x of d.added || []) {
      const info = docMap.get(x.name?.trim()?.toLowerCase());
      list.push({ type: "document", name: x.name, section: x.section, weight: x.weight ?? info?.weight, isRequired: info?.isRequired });
    }
    return list;
  }, [s.added, d.added, docMap]);

  const removedItems = useMemo(
    () => [
      ...(s.removed || []).map((n) => ({ type: "section", text: `Section: ${n}` })),
      ...(d.removed || []).map((x) => ({ type: "document", name: x.name, section: x.section, weight: x.weight })),
    ],
    [s.removed, d.removed],
  );

  const renamedItems = useMemo(
    () => [...(s.renamed || []).map((x) => `Section: ${x.from} → ${x.to}`), ...(d.renamed || []).map((x) => `${x.section} › ${x.from} → ${x.to}`)],
    [s.renamed, d.renamed],
  );

  const fieldsChangedItems = useMemo(
    () =>
      (d.fieldsChanged || []).map((x) => {
        const bits = [];
        if (x.added?.length) bits.push(`+${x.added.join(", ")}`);
        if (x.removed?.length) bits.push(`−${x.removed.join(", ")}`);
        if (x.changed?.length) bits.push(`~${x.changed.map((c) => c.fieldName).join(", ")}`);
        return `${x.name} (${bits.join("  ")})`;
      }),
    [d.fieldsChanged],
  );

  const weightChangedItems = useMemo(
    () =>
      (d.weightChanged || []).map((x) => {
        const from = Number(x.from ?? 0);
        const to = Number(x.to ?? 0);
        return { name: x.name, section: x.section, from, to, diff: x.diff != null ? Number(x.diff) : to - from };
      }),
    [d.weightChanged],
  );

  if (!summary) return null;

  // The distribution table answers "so what does it all add up to now?", which
  // only has an answer once weights have actually moved.
  const hasWeightActivity = weightChangedItems.length > 0 || addedItems.some((i) => i.type === "document" && i.weight != null);

  const hasAnything = summary.regionChanged || addedItems.length || removedItems.length || renamedItems.length || fieldsChangedItems.length || weightChangedItems.length;
  if (!hasAnything) return null;

  return (
    <div className="rounded-lg border border-border-primary/60 p-3.5">
      <p className="mb-2.5 text-[12.5px] font-medium text-text-primary">What changed</p>
      <div className="flex flex-col gap-3">
        {summary.regionChanged && (
          <div>
            <p className="text-[11px] font-semibold text-tone-amber">Region changed</p>
            <p className="mt-0.5 text-[11.5px] text-text-secondary">Every document&apos;s AI prompt is rebuilt from the region</p>
          </div>
        )}

        {addedItems.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-tone-emerald">Added</p>
            <div className="mt-1 flex flex-col gap-1.5">
              {addedItems.map((item, idx) =>
                item.type === "section" ? (
                  <p key={idx} className="text-[11.5px] text-text-secondary">
                    {item.text}
                  </p>
                ) : (
                  <div key={idx} className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-text-secondary">
                    <span>
                      {item.section} › <strong className="font-medium text-text-primary">{item.name}</strong>
                    </span>
                    {item.weight != null && (
                      <span className="inline-flex items-center rounded-md bg-tone-emerald-soft/40 px-1.5 py-0.5 text-[10.5px] font-semibold text-tone-emerald">
                        +{item.weight}% weight
                      </span>
                    )}
                    {item.isRequired && (
                      <span className="inline-flex items-center rounded bg-background-icon px-1.5 py-0.5 text-[10px] text-text-secondary">Required</span>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {removedItems.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-tone-rose">Removed</p>
            <div className="mt-1 flex flex-col gap-1.5">
              {removedItems.map((item, idx) =>
                item.type === "section" ? (
                  <p key={idx} className="text-[11.5px] text-text-secondary">
                    {item.text}
                  </p>
                ) : (
                  <div key={idx} className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-text-secondary">
                    <span>
                      {item.section} › <strong className="font-medium text-text-primary">{item.name}</strong>
                    </span>
                    {item.weight != null && <span className="text-[10.5px] text-tone-rose">(was {item.weight}% weight)</span>}
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {renamedItems.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-tone-amber">Renamed</p>
            <ul className="mt-0.5 flex flex-col gap-0.5">
              {renamedItems.map((item) => (
                <li key={item} className="text-[11.5px] text-text-secondary">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {fieldsChangedItems.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-tone-amber">Fields changed</p>
            <ul className="mt-0.5 flex flex-col gap-0.5">
              {fieldsChangedItems.map((item) => (
                <li key={item} className="text-[11.5px] text-text-secondary">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {weightChangedItems.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-text-primary">Weights changed</p>
            <div className="mt-1 flex flex-col gap-1.5">
              {weightChangedItems.map((x, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 text-[11.5px] text-text-secondary">
                  <span className="font-medium text-text-primary">{x.name}:</span>
                  <span>
                    {x.from}% → <strong className="font-semibold text-text-primary">{x.to}%</strong>
                  </span>
                  <span
                    className={cn(
                      "inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10.5px] font-medium",
                      x.diff < 0 ? "bg-tone-amber-soft/30 text-tone-amber" : "bg-tone-emerald-soft/30 text-tone-emerald",
                    )}
                  >
                    {x.diff < 0 ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
                    {x.diff > 0 ? `+${x.diff}%` : `${x.diff}%`}
                    {x.diff < 0 && <span className="ml-0.5 opacity-80">(reallocated)</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasWeightActivity && allDraftDocs.length > 0 && (
          <div className="mt-1 rounded-md border border-border-primary/50 bg-background-primary/40 p-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-text-secondary">Updated Weight Distribution</span>
              <span className={cn("text-[11px] font-semibold", totalWeight === 100 ? "text-tone-emerald" : "text-tone-amber")}>Total: {totalWeight}%</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allDraftDocs.map((doc, idx) => (
                <div
                  key={`${doc.section}_${doc.name}_${idx}`}
                  className="flex items-center gap-1.5 rounded-md border border-border-primary/40 bg-background-secondary px-2 py-1 text-[11px]"
                >
                  <span className="max-w-[130px] truncate text-text-secondary" title={`${doc.section} › ${doc.name}`}>
                    {doc.name}
                  </span>
                  <span className="font-semibold text-text-primary">{doc.weight}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistReapplyModal;
